import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Pagination from '../../utils/pagination2';
import Breadcrumb from '../../utils/breadcrumbs';
import { FiUserPlus, FiEye, FiTrash2 } from 'react-icons/fi';
import CreateUserOffcanvas from '../../components/users/create-offcanvas';
import { ListLoader } from '../../utils/loader';
import ConfirmationModal from '../../ui/confirmation-modal';
import toast from 'react-hot-toast';
import api from '../../libs/axios';

enum UserStatus {
    PENDING = 'pending',
    VERIFIED = 'verified',
    SUSPENDED = 'suspended',
    INITIATED = 'initiated',
    // DEACTIVATED = 'deactivated'
}

export interface IUser {
    id: number;
    phone_number: string;
    role_id: number;
    status: string;
    created_at?: Date;
    updated_at?: Date;
    role: string;
    details?: {
        first_name: string;
        last_name: string;
        email: string;
    }
}

interface IRole {
    id: number;
    name: string;
    description?: string;
}

const Users: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useState<Record<string, string>>({});
    const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
    const [showAddUserCanvas, setShowAddUserCanvas] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [users, setUsers] = useState<IUser[]>([]);
    const [roles, setRoles] = useState<IRole[]>([]);
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Pagination state variables
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 1,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null,
    });

    // Filter users based on search parameters
    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const nameMatch = !searchParams.name ||
                `${user.details?.first_name || ''} ${user.details?.last_name || ''}`
                    .toLowerCase()
                    .includes(searchParams.name.toLowerCase());

            const phoneMatch = !searchParams.phone_number ||
                user.phone_number.includes(searchParams.phone_number);

            // Fixed: Use role_id for filtering instead of role name
            const roleMatch = !searchParams.role_id ||
                user.role_id === Number(searchParams.role_id);

            const statusMatch = !searchParams.status ||
                user.status.toLowerCase() === searchParams.status.toLowerCase();

            return nameMatch && phoneMatch && statusMatch && roleMatch;
        });
    }, [users, searchParams]);

    // Fetch users with search params
    const fetchUsers = async (useSearchParams = false) => {
        setLoading(true);
        try {
            let url = `/users?page=${currentPage}&limit=${limit}`;

            // Add search parameters to API call if they exist and useSearchParams is true
            if (useSearchParams && Object.keys(searchParams).length > 0) {
                const params = new URLSearchParams();
                if (searchParams.name) params.append('name', searchParams.name);
                if (searchParams.phone_number) params.append('phone_number', searchParams.phone_number);
                if (searchParams.role_id) params.append('role_id', searchParams.role_id);
                if (searchParams.status) params.append('status', searchParams.status);
                url += `&${params.toString()}`;
            }

            const response = await api.get(url);
            setUsers(response.data.data);
            setPagination(response.data.pagination);
        } catch (error) {
            console.log(error);
            toast.error('Error fetching users');
        } finally {
            setLoading(false);
        }
    };

    // Fetch users on page/limit change
    useEffect(() => {
        fetchUsers();
    }, [currentPage, limit, searchParams?.role_id, searchParams?.status]);

    // Search in database when filtered list is empty but search params exist
    useEffect(() => {
        if (Object.keys(searchParams).length > 0 && filteredUsers.length === 0 && !loading) {
            fetchUsers(true);
        }
    }, [filteredUsers.length, searchParams]);

    const fetchRoles = async () => {
        try {
            const response = await api.get(`/roles/active`);
            setRoles(response.data);
        } catch (error: any) {
            console.error("Error fetching roles:", error);
            toast.error("Failed to fetch roles");
        }
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    // Handle search
    const handleSearchChange = (column: string, value: string) => {
        setSearchParams(prev => {
            const newParams = { ...prev };
            if (value) {
                newParams[column] = value;
            } else {
                delete newParams[column];
            }
            return newParams;
        });
        setCurrentPage(1);

        // If search is cleared (empty value), fetch fresh data from API
        if (!value) {
            fetchUsers(false);
        }
    };

    // Get status style
    const getStatusStyle = (status: string) => {
        switch (status) {
            case UserStatus.PENDING: return 'bg-yellow-100 text-yellow-800';
            case UserStatus.VERIFIED: return 'bg-green-100 text-green-800';
            case UserStatus.SUSPENDED: return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Handle limit change
    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    // Handle delete
    const handleDeleteClick = (user: IUser, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedUser(user);
        setDeleteConfirmOpen(true);
    };

    // Handle confirm delete
    const handleDeleteConfirm = async () => {
        if (selectedUser) {
            setDeleteLoading(true);
            try {
                await api.delete(`/users/${selectedUser.id}`);
                toast.success('User deleted successfully');
                fetchUsers();
            } catch (err) {
                toast.error('Error deleting user');
            } finally {
                setDeleteLoading(false);
                setDeleteConfirmOpen(false);
                setSelectedUser(null);
            }
        }
    };

    // Handle row click to navigate to user details
    const handleRowClick = (userId: number) => {
        navigate('/users/view', { state: { userId } });
    };

    return (
        <div className="w-full min-h-screen bg-white p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Users
                </h2>
                <Breadcrumb />
            </div>
            <div className="w-full bg-white rounded-lg shadow border">
                <div className="flex justify-end p-4 w-full rounded-xl sticky top-0 bg-white">
                    <button
                        onClick={() => setShowAddUserCanvas(true)}
                        className="flex gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg items-center sm:px-4"
                    >
                        <FiUserPlus className="w-5 h-5 sm:mr-0" />
                        <span className="hidden sm:block">Add User</span>
                    </button>
                </div>

                <div className="overflow-x-auto min-h-[70vh]">
                    <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-0">
                            <tr>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Full Name
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Phone Number
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Role
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Status
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr className="bg-gray-50">
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Name"
                                        value={searchParams.name || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('name', e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Phone Number"
                                        value={searchParams.phone_number || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('phone_number', e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    {roles.length > 0 ? (
                                        <select
                                            value={searchParams.role_id || ''}
                                            className="w-full p-2 h-10 border rounded text-sm capitalize"
                                            onChange={(e) => handleSearchChange('role_id', e.target.value)}
                                        >
                                            <option value="">All</option>
                                            {roles.map((role) => (
                                                <option key={role.id} value={role.id} className="capitalize">
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span>Loading...</span>
                                    )}
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <select
                                        value={searchParams.status || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('status', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value={UserStatus.PENDING}>Pending</option>
                                        <option value={UserStatus.VERIFIED}>Verified</option>
                                        <option value={UserStatus.SUSPENDED}>Suspended</option>
                                        <option value={UserStatus.INITIATED}>Initiated</option>
                                    </select>
                                </td>
                                <td className="px-6 py-2 border-r"></td>
                            </tr>
                            {loading ? (
                                <ListLoader />
                            ) : filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center py-8 text-gray-500">
                                        No users found
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map(user => (
                                    <tr
                                        key={user.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleRowClick(user.id)}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <div className="flex font-semibold items-center">
                                                {user.details?.first_name || user.details?.last_name ? `${user.details?.first_name} ${user.details?.last_name}` : 'Unknown User'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            {user.phone_number}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className="bg-yellow-100 capitalize w-fit ml-1.5 px-3 py-1 text-xs rounded-full flex items-center text-yellow-800">
                                                {user.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className={`px-2 py-1 capitalize text-xs rounded-full ${getStatusStyle(user.status)}`}>
                                                {user.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div
                                                className="flex space-x-3"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Link
                                                    to='/users/view'
                                                    state={{ userId: user.id }}
                                                    className="text-primary-600 hover:text-primary-700"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <FiEye className="w-5 h-5" />
                                                </Link>
                                                <button
                                                    onClick={(e) => handleDeleteClick(user, e)}
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    <FiTrash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex w-full bg-white sticky rounded-lg bottom-0">
                    <Pagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                    />
                </div>

                <CreateUserOffcanvas
                    roles={roles}
                    fetchUsers={fetchUsers}
                    showCreateUserCanvas={showAddUserCanvas}
                    setShowCreateUserCanvas={setShowAddUserCanvas}
                />

                {deleteConfirmOpen && (
                    <ConfirmationModal
                        isOpen={deleteConfirmOpen}
                        onClose={() => setDeleteConfirmOpen(false)}
                        onConfirm={handleDeleteConfirm}
                        title="Delete User"
                        description="Are you sure you want to delete this user? This action cannot be undone."
                        loading={deleteLoading}
                    />
                )}
            </div>
        </div>
    );
};

export default Users;